/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check, Users, DollarSign, Percent } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCreateOrganization } from '@/hooks/api/useOrganizations';
import { usePartners } from '@/hooks/api/usePartners';
import { useCurrencies } from '@/hooks/api/useCurrencies';
import { usePricingTiers, useVolumeDiscounts } from '@/hooks/api/useModulePricing';
import { useNotification } from '@/hooks';
import { isApiError, getValidationErrors, formatErrorForDisplay } from '@/services/api/errors';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { countries, getCountryConfig } from '@/data/countries';
import { useCountries } from '@/hooks/useCountries';
import { useGeolocation } from '@/hooks/useGeolocation';
import { ModulePricingTier, VolumeDiscountRule } from '@/types/superadmin.types';

// Types
type CompanySize = '1-10' | '11-50' | '51-200' | '200+';

// Module selection for the form
interface ModuleSelectionItem {
  moduleCode: string;
  tierCode: string;
  userCount: number;
  selected: boolean;
}

interface CreateOrgForm {
  // Organization
  name: string;
  legalName: string;
  taxId: string;
  rccm: string;
  industry: string;
  companySize: CompanySize;
  // Admin User
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string;
  // Contact Information
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  address: string;
  city: string;
  country: string;
  // Subscription - Module-based
  moduleSelections: ModuleSelectionItem[];
  billingCycle: 'MONTHLY' | 'ANNUAL';
  currency: string;
  startTrial: boolean;
  partnerId: string;
  // Notes
  notes: string;
}

// Module icons (static)
const MODULE_ICONS: Record<string, string> = {
  CORE: '⚙️',
  COMPTA: '📊',
  HR: '👥',
  CRM: '🤝',
  PM: '📋',
};

// Helper function to get translated module info
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getModuleInfo = (moduleCode: string, t: (key: string, options?: any) => string) => ({
  name: t(`createOrganization.modules.${moduleCode}.name`, { defaultValue: moduleCode }),
  icon: MODULE_ICONS[moduleCode] || '📦',
  description: t(`createOrganization.modules.${moduleCode}.description`, { defaultValue: '' }),
});

// Main Create Organization Page
export default function CreateOrganizationPage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const createMutation = useCreateOrganization();
  const { data: partnersData } = usePartners({ status: 'ACTIVE', size: 100 });
  const partners = partnersData?.content || [];
  const { data: currencies = [] } = useCurrencies();
  const { data: pricingTiers = [] } = usePricingTiers();
  const { data: volumeDiscounts = [] } = useVolumeDiscounts();
  const { success, error: showError } = useNotification();

  // Geolocation for country-specific defaults and placeholders
  const { country: detectedCountry, countryConfig: detectedConfig, isLoading: geoLoading } = useGeolocation();
  const { countryOptions: localizedCountryOptions } = useCountries();

  // Field-level errors from API (for highlighting)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // User count validation errors per module
  const [userCountErrors, setUserCountErrors] = useState<Record<string, string>>({});

  // Track if geolocation defaults have been applied
  const [geoApplied, setGeoApplied] = useState(false);

  // Group tiers by module code
  const tiersByModule = useMemo(() => {
    const grouped: Record<string, ModulePricingTier[]> = {};
    pricingTiers.filter(t => t.isActive && t.isPublic).forEach(tier => {
      if (!grouped[tier.moduleCode]) {
        grouped[tier.moduleCode] = [];
      }
      grouped[tier.moduleCode].push(tier);
    });
    // Sort tiers within each module by sortOrder
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.sortOrder - b.sortOrder);
    });
    return grouped;
  }, [pricingTiers]);

  // Get available module codes
  const availableModules = useMemo(() => Object.keys(tiersByModule), [tiersByModule]);

  // Initialize module selections when tiers are loaded
  const [moduleSelectionsInitialized, setModuleSelectionsInitialized] = useState(false);

  const [form, setForm] = useState<CreateOrgForm>({
    name: '',
    legalName: '',
    taxId: '',
    rccm: '',
    industry: '',
    companySize: '1-10',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    address: '',
    city: '',
    country: '', // Will be set from geolocation
    moduleSelections: [],
    billingCycle: 'MONTHLY',
    currency: '', // Will be set from geolocation
    startTrial: true,
    partnerId: '',
    notes: '',
  });

  // Initialize module selections when tiers are loaded
  useEffect(() => {
    if (availableModules.length > 0 && !moduleSelectionsInitialized) {
      const initialSelections: ModuleSelectionItem[] = availableModules.map(moduleCode => {
        const tiers = tiersByModule[moduleCode];
        const defaultTier = tiers.find(t => t.tierCode === 'PRO') || tiers[0];
        return {
          moduleCode,
          tierCode: defaultTier?.tierCode || 'BASIC',
          userCount: 5, // Default user count
          selected: moduleCode === 'CORE', // CORE is selected by default (core module)
        };
      });
      setForm(prev => ({ ...prev, moduleSelections: initialSelections }));
      setModuleSelectionsInitialized(true);
    }
  }, [availableModules, tiersByModule, moduleSelectionsInitialized]);

  // Set defaults from geolocation when detected (only once)
  useEffect(() => {
    if (detectedCountry && detectedConfig && !geoApplied) {
      setForm(prev => ({
        ...prev,
        country: detectedCountry.name,
        currency: detectedConfig.defaultCurrency,
      }));
      setGeoApplied(true);
    }
  }, [detectedCountry, detectedConfig, geoApplied]);

  // Get country config based on selected country (for dynamic placeholders)
  const selectedCountryConfig = useMemo(() => {
    const selectedCountry = countries.find(c => c.name === form.country);
    if (selectedCountry) {
      return getCountryConfig(selectedCountry.code);
    }
    return detectedConfig;
  }, [form.country, detectedConfig]);

  // ISO code for the selected country (for PhoneInput flag)
  const selectedCountryCode = useMemo(() => {
    const selectedCountry = countries.find(c => c.name === form.country);
    return selectedCountry?.code || detectedCountry?.code || 'GN';
  }, [form.country, detectedCountry]);

  // Module selection handlers
  const handleModuleToggle = (moduleCode: string) => {
    // CORE cannot be unchecked - it's the core platform module
    if (moduleCode === 'CORE') return;

    setForm(prev => ({
      ...prev,
      moduleSelections: prev.moduleSelections.map(ms =>
        ms.moduleCode === moduleCode ? { ...ms, selected: !ms.selected } : ms
      ),
    }));
  };

  const handleModuleTierChange = (moduleCode: string, tierCode: string) => {
    setForm(prev => ({
      ...prev,
      moduleSelections: prev.moduleSelections.map(ms =>
        ms.moduleCode === moduleCode ? { ...ms, tierCode } : ms
      ),
    }));
  };

  const handleModuleUserCountChange = (moduleCode: string, userCount: number) => {
    const moduleSelection = form.moduleSelections.find(ms => ms.moduleCode === moduleCode);
    const selectedTier = moduleSelection ? tiersByModule[moduleCode]?.find(t => t.tierCode === moduleSelection.tierCode) : null;
    const maxUsers = selectedTier?.maxUsers;

    // Validate against max users
    if (maxUsers && userCount > maxUsers) {
      setUserCountErrors(prev => ({
        ...prev,
        [moduleCode]: t('createOrganization.validation.userCountMax', { max: maxUsers }),
      }));
    } else {
      setUserCountErrors(prev => {
        const updated = { ...prev };
        delete updated[moduleCode];
        return updated;
      });
    }

    setForm(prev => ({
      ...prev,
      moduleSelections: prev.moduleSelections.map(ms =>
        ms.moduleCode === moduleCode ? { ...ms, userCount: Math.max(1, userCount) } : ms
      ),
    }));
  };

  // Calculate pricing
  const pricingCalculation = useMemo(() => {
    const selectedModules = form.moduleSelections.filter(ms => ms.selected);
    const isAnnual = form.billingCycle === 'ANNUAL';

    let subtotal = 0;
    const lineItems: Array<{
      moduleCode: string;
      moduleName: string;
      tierCode: string;
      tierName: string;
      pricingModel: string;
      userCount?: number;
      unitPrice: number;
      lineTotal: number;
    }> = [];

    selectedModules.forEach(ms => {
      const tiers = tiersByModule[ms.moduleCode];
      const tier = tiers?.find(t => t.tierCode === ms.tierCode);
      if (!tier) return;

      const moduleInfo = getModuleInfo(ms.moduleCode, t);
      let unitPrice = 0;
      let lineTotal = 0;

      if (tier.pricingModel === 'PER_USER') {
        unitPrice = isAnnual ? tier.perUserAnnualUsd / 12 : tier.perUserMonthlyUsd;
        lineTotal = unitPrice * ms.userCount;
      } else {
        unitPrice = isAnnual ? tier.annualPriceUsd / 12 : tier.monthlyPriceUsd;
        lineTotal = unitPrice;
      }

      lineItems.push({
        moduleCode: ms.moduleCode,
        moduleName: moduleInfo.name,
        tierCode: ms.tierCode,
        tierName: tier.tierName,
        pricingModel: tier.pricingModel,
        userCount: tier.pricingModel === 'PER_USER' ? ms.userCount : undefined,
        unitPrice,
        lineTotal,
      });

      subtotal += lineTotal;
    });

    // Find applicable volume discount
    const paidModuleCount = selectedModules.length;
    const applicableDiscount = volumeDiscounts
      .filter(d => d.isActive && paidModuleCount >= d.minModules && (!d.maxModules || paidModuleCount <= d.maxModules))
      .sort((a, b) => b.discountPercent - a.discountPercent)[0];

    const discountPercent = applicableDiscount?.discountPercent || 0;
    const discountAmount = subtotal * (discountPercent / 100);
    const total = subtotal - discountAmount;

    return {
      lineItems,
      subtotal,
      discountPercent,
      discountAmount,
      discountRule: applicableDiscount?.ruleName,
      total,
      moduleCount: paidModuleCount,
      isAnnual,
    };
  }, [form.moduleSelections, form.billingCycle, tiersByModule, volumeDiscounts]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Clear previous field errors
    setFieldErrors({});

    // Prepare module selections for backend
    const selectedModules = form.moduleSelections
      .filter(ms => ms.selected)
      .map(ms => ({
        moduleCode: ms.moduleCode,
        tierCode: ms.tierCode,
        userCount: ms.userCount,
      }));

    createMutation.mutate({
      name: form.name,
      legalName: form.legalName || undefined,
      taxId: form.taxId || undefined,
      rccm: form.rccm || undefined,
      industry: form.industry,
      companySize: form.companySize,
      adminFirstName: form.adminFirstName,
      adminLastName: form.adminLastName,
      adminEmail: form.adminEmail,
      adminPhone: form.adminPhone || undefined,
      // Contact Information
      primaryContactName: form.primaryContactName || undefined,
      primaryContactEmail: form.primaryContactEmail || undefined,
      primaryContactPhone: form.primaryContactPhone || undefined,
      address: form.address || undefined,
      city: form.city || undefined,
      country: form.country || undefined,
      // Subscription - Module-based
      moduleSelections: selectedModules.length > 0 ? selectedModules : undefined,
      billingCycle: form.billingCycle,
      currency: form.currency,
      startTrial: form.startTrial,
      // Partner ID is stored separately - backend handles referral tracking
      referralCode: form.partnerId || undefined,
      // Notes
      notes: form.notes || undefined,
    }, {
      onSuccess: () => {
        // Show success notification
        success(t('createOrganization.messages.success'));
        navigate('/superadmin/organizations');
      },
      onError: (error) => {
        // Extract and set field-level errors for highlighting
        if (isApiError(error)) {
          const errors = getValidationErrors(error);
          if (errors) {
            setFieldErrors(errors);
          }
          // Show error notification with translated message
          const { message } = formatErrorForDisplay(error, t);
          showError(message);
        } else {
          showError(t('createOrganization.messages.unexpectedError'));
        }
      },
    });
  };

  const industryOptions = useMemo(() => [
    { value: '', label: t('createOrganization.industry.select') },
    { value: 'retail', label: t('createOrganization.industry.retail') },
    { value: 'import-export', label: t('createOrganization.industry.importExport') },
    { value: 'manufacturing', label: t('createOrganization.industry.manufacturing') },
    { value: 'technology', label: t('createOrganization.industry.technology') },
    { value: 'services', label: t('createOrganization.industry.services') },
    { value: 'finance', label: t('createOrganization.industry.finance') },
    { value: 'healthcare', label: t('createOrganization.industry.healthcare') },
    { value: 'education', label: t('createOrganization.industry.education') },
    { value: 'other', label: t('createOrganization.industry.other') },
  ], [t]);

  const companySizeOptions = useMemo(() => [
    { value: '1-10', label: t('createOrganization.companySize.1-10') },
    { value: '11-50', label: t('createOrganization.companySize.11-50') },
    { value: '51-200', label: t('createOrganization.companySize.51-200') },
    { value: '200+', label: t('createOrganization.companySize.200+') },
  ], [t]);

  const billingOptions = useMemo(() => [
    { value: 'MONTHLY', label: t('createOrganization.billing.monthly') },
    { value: 'ANNUAL', label: t('createOrganization.billing.annual') },
  ], [t]);

  const currencyOptions = useMemo(() =>
    currencies.map(c => ({
      value: c.code,
      label: `${c.code} - ${c.name} (${c.symbol})`
    })),
  [currencies]);

  const countryOptions = localizedCountryOptions;

  return (
    <div className="create-org-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('createOrganization.title')}</h1>
          <p className="page-subtitle">{t('createOrganization.subtitle')}</p>
        </div>
        <div className="page-actions">
          <button
            type="button"
            onClick={() => navigate('/superadmin/organizations')}
            className="btn btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="create-org-form"
            disabled={createMutation.isPending || !form.name || !form.adminEmail}
            className="btn btn-primary"
          >
            {createMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              '📧'
            )}
            <span className="btn-text">{t('createOrganization.submitButton')}</span>
          </button>
        </div>
      </div>

      <form id="create-org-form" onSubmit={handleSubmit}>
        {/* Organization Information */}
        <div className="form-section">
          <div className="form-section-title">🏢 {t('createOrganization.sections.organization')}</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.organizationName')} <span className="required">*</span></label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className={`form-input ${fieldErrors.name ? 'has-error' : ''}`}
                placeholder={t('createOrganization.placeholders.organizationName')}
              />
              {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('organizationDetail.fields.legalName')}</label>
              <input
                type="text"
                name="legalName"
                value={form.legalName}
                onChange={handleChange}
                className="form-input"
                placeholder={t('createOrganization.placeholders.legalName')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{selectedCountryConfig?.taxIdLabel || t('organizationDetail.fields.taxId')}</label>
              <input
                type="text"
                name="taxId"
                value={form.taxId}
                onChange={handleChange}
                className="form-input mono"
                placeholder={selectedCountryConfig?.taxIdPlaceholder || 'XXXX-XXXXXXXX'}
              />
            </div>
            {selectedCountryConfig?.businessRegistryLabel && (
              <div className="form-group">
                <label className="form-label">{selectedCountryConfig.businessRegistryLabel}</label>
                <input
                  type="text"
                  name="rccm"
                  value={form.rccm}
                  onChange={handleChange}
                  className="form-input mono"
                  placeholder={selectedCountryConfig.businessRegistryPlaceholder || ''}
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">{t('organizationDetail.fields.industry')}</label>
              <select
                name="industry"
                value={form.industry}
                onChange={handleChange}
                className="form-select"
              >
                {industryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.companySize')}</label>
              <select
                name="companySize"
                value={form.companySize}
                onChange={handleChange}
                className="form-select"
              >
                {companySizeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Admin User */}
        <div className="form-section">
          <div className="form-section-title">👤 {t('createOrganization.sections.adminUser')}</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.firstName')} <span className="required">*</span></label>
              <input
                type="text"
                name="adminFirstName"
                value={form.adminFirstName}
                onChange={handleChange}
                className="form-input"
                placeholder={t('createOrganization.placeholders.firstName')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.lastName')} <span className="required">*</span></label>
              <input
                type="text"
                name="adminLastName"
                value={form.adminLastName}
                onChange={handleChange}
                className="form-input"
                placeholder={t('createOrganization.placeholders.lastName')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.emailAddress')} <span className="required">*</span></label>
              <input
                type="email"
                name="adminEmail"
                value={form.adminEmail}
                onChange={handleChange}
                className={`form-input ${fieldErrors.adminEmail || fieldErrors.email ? 'has-error' : ''}`}
                placeholder={t('createOrganization.placeholders.email')}
              />
              {(fieldErrors.adminEmail || fieldErrors.email) ? (
                <span className="field-error">{fieldErrors.adminEmail || fieldErrors.email}</span>
              ) : (
                <div className="form-help">{t('createOrganization.help.invitationEmail')}</div>
              )}
            </div>
            <div className="form-group">
              <PhoneInput
                label={t('createOrganization.fields.phoneNumber')}
                mask={selectedCountryConfig?.phonePlaceholder || '+XXX XXX XXX XXX'}
                countryCode={selectedCountryCode}
                value={form.adminPhone}
                onChange={(v) => setForm(prev => ({ ...prev, adminPhone: v }))}
                name="adminPhone"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <div className="form-section-title">📍 {t('createOrganization.sections.contact')}</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.contactName')}</label>
              <input
                type="text"
                name="primaryContactName"
                value={form.primaryContactName}
                onChange={handleChange}
                className={`form-input ${fieldErrors.primaryContactName ? 'has-error' : ''}`}
                placeholder={t('createOrganization.placeholders.contactName')}
              />
              {fieldErrors.primaryContactName && <span className="field-error">{fieldErrors.primaryContactName}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.contactEmail')}</label>
              <input
                type="email"
                name="primaryContactEmail"
                value={form.primaryContactEmail}
                onChange={handleChange}
                className={`form-input ${fieldErrors.primaryContactEmail ? 'has-error' : ''}`}
                placeholder={t('createOrganization.placeholders.contactEmail')}
              />
              {fieldErrors.primaryContactEmail && <span className="field-error">{fieldErrors.primaryContactEmail}</span>}
            </div>
            <div className="form-group">
              <PhoneInput
                label={t('createOrganization.fields.contactPhone')}
                mask={selectedCountryConfig?.phonePlaceholder || '+XXX XXX XXX XXX'}
                countryCode={selectedCountryCode}
                value={form.primaryContactPhone}
                onChange={(v) => setForm(prev => ({ ...prev, primaryContactPhone: v }))}
                error={fieldErrors.primaryContactPhone}
                name="primaryContactPhone"
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.address')}</label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                className={`form-input ${fieldErrors.address ? 'has-error' : ''}`}
                placeholder={t('createOrganization.placeholders.address')}
              />
              {fieldErrors.address && <span className="field-error">{fieldErrors.address}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.city')}</label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                className={`form-input ${fieldErrors.city ? 'has-error' : ''}`}
                placeholder={selectedCountryConfig?.capital || t('createOrganization.placeholders.city')}
              />
              {fieldErrors.city && <span className="field-error">{fieldErrors.city}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.country')}</label>
              <SearchableSelect
                options={countryOptions}
                value={form.country}
                onChange={(value) => setForm(prev => ({ ...prev, country: value }))}
                placeholder={t('createOrganization.placeholders.selectCountry')}
                searchPlaceholder={t('createOrganization.placeholders.searchCountry')}
                noResultsText={t('common.noResults')}
                error={fieldErrors.country}
                clearable
              />
            </div>
          </div>
        </div>

        {/* Module Selection */}
        <div className="form-section">
          <div className="form-section-title">📦 {t('createOrganization.sections.modules', 'Select Modules')}</div>
          <p className="section-description">{t('createOrganization.modulesDescription', 'Choose which modules to include in the subscription. Each module has its own pricing tier.')}</p>

          {/* Billing Cycle Toggle */}
          <div className="billing-cycle-toggle">
            <button
              type="button"
              className={`cycle-btn ${form.billingCycle === 'MONTHLY' ? 'active' : ''}`}
              onClick={() => setForm(prev => ({ ...prev, billingCycle: 'MONTHLY' }))}
            >
              {t('createOrganization.billing.monthly')}
            </button>
            <button
              type="button"
              className={`cycle-btn ${form.billingCycle === 'ANNUAL' ? 'active' : ''}`}
              onClick={() => setForm(prev => ({ ...prev, billingCycle: 'ANNUAL' }))}
            >
              {t('createOrganization.billing.annual')} <span className="discount-badge">-10%</span>
            </button>
          </div>

          {/* CORE Module - Heart of the Suite */}
          {(() => {
            const coreModule = form.moduleSelections.find(ms => ms.moduleCode === 'CORE');
            if (!coreModule) return null;
            const coreInfo = getModuleInfo('CORE', t);
            return (
              <div className="core-module-hero">
                <div className="core-module-card">
                  <div className="core-icon-wrapper">
                    <span className="core-icon">{coreInfo.icon}</span>
                    <div className="core-pulse"></div>
                  </div>
                  <div className="core-content">
                    <div className="core-title">
                      {coreInfo.name}
                      <span className="core-badge">{t('createOrganization.requiredModule', 'Required')}</span>
                    </div>
                    <div className="core-description">{coreInfo.description}</div>
                    <div className="core-included">{t('createOrganization.coreIncluded', 'Included with all subscriptions')}</div>
                  </div>
                  <div className="core-check">
                    <Check className="w-6 h-6" />
                  </div>
                </div>
                <div className="core-connector">
                  <div className="connector-line"></div>
                  <div className="connector-dots">
                    <span></span><span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Add-on Modules - 2x2 Grid */}
          <div className="addon-modules-label">{t('createOrganization.addonModules', 'Add-on Modules')}</div>
          <div className="module-cards">
            {form.moduleSelections.filter(ms => ms.moduleCode !== 'CORE').map(ms => {
              const moduleInfo = getModuleInfo(ms.moduleCode, t);
              const moduleTiers = tiersByModule[ms.moduleCode] || [];
              const selectedTier = moduleTiers.find(t => t.tierCode === ms.tierCode);
              const isPerUser = selectedTier?.pricingModel === 'PER_USER';

              return (
                <div
                  key={ms.moduleCode}
                  className={`module-card ${ms.selected ? 'selected' : ''}`}
                >
                  <div className="module-header">
                    <label className="module-checkbox">
                      <input
                        type="checkbox"
                        checked={ms.selected}
                        onChange={() => handleModuleToggle(ms.moduleCode)}
                      />
                      <span className="checkmark">
                        {ms.selected && <Check className="w-4 h-4" />}
                      </span>
                    </label>
                    <div className="module-info">
                      <span className="module-icon">{moduleInfo.icon}</span>
                      <div>
                        <div className="module-name">{moduleInfo.name}</div>
                        <div className="module-desc">{moduleInfo.description}</div>
                      </div>
                    </div>
                  </div>

                  {ms.selected && (
                    <div className="module-config">
                      <div className="config-row">
                        <label className="config-label">{t('createOrganization.fields.tier', 'Tier')}</label>
                        <select
                          value={ms.tierCode}
                          onChange={(e) => handleModuleTierChange(ms.moduleCode, e.target.value)}
                          className="form-select tier-select"
                        >
                          {moduleTiers.map(tier => (
                            <option key={tier.tierCode} value={tier.tierCode}>
                              {tier.tierName} - ${tier.pricingModel === 'PER_USER'
                                ? `${tier.perUserMonthlyUsd}${t('createOrganization.pricing.perUserPerMonth')}`
                                : `${tier.monthlyPriceUsd}${t('createOrganization.pricing.perMonth')}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {isPerUser && (
                        <div className="config-row">
                          <label className="config-label">
                            <Users className="w-4 h-4" />
                            {t('createOrganization.fields.users', 'Users')}
                          </label>
                          <div className="user-count-input">
                            <button
                              type="button"
                              onClick={() => handleModuleUserCountChange(ms.moduleCode, ms.userCount - 1)}
                              className="count-btn"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={ms.userCount}
                              onChange={(e) => handleModuleUserCountChange(ms.moduleCode, parseInt(e.target.value) || 1)}
                              min="1"
                              className={`count-input ${userCountErrors[ms.moduleCode] ? 'input-error' : ''}`}
                            />
                            <button
                              type="button"
                              onClick={() => handleModuleUserCountChange(ms.moduleCode, ms.userCount + 1)}
                              className="count-btn"
                            >
                              +
                            </button>
                          </div>
                          {selectedTier?.maxUsers && (
                            <span className="limit-info">{t('createOrganization.maxUsers', { max: selectedTier.maxUsers })}</span>
                          )}
                          {userCountErrors[ms.moduleCode] && (
                            <div className="validation-error">{userCountErrors[ms.moduleCode]}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pricing Summary */}
          {pricingCalculation.moduleCount > 0 && (
            <div className="pricing-summary">
              <div className="summary-title">
                <DollarSign className="w-5 h-5" />
                {t('createOrganization.pricingSummary', 'Pricing Summary')}
              </div>

              <div className="line-items">
                {pricingCalculation.lineItems.map(item => (
                  <div key={item.moduleCode} className="line-item">
                    <span className="item-name">
                      {item.moduleName} ({item.tierName})
                      {item.userCount && <span className="user-count"> × {item.userCount} {t('createOrganization.pricing.users')}</span>}
                    </span>
                    <span className="item-price">${item.lineTotal.toFixed(2)}{t('createOrganization.pricing.perMonth')}</span>
                  </div>
                ))}
              </div>

              <div className="summary-line subtotal">
                <span>{t('createOrganization.subtotal', 'Subtotal')}</span>
                <span>${pricingCalculation.subtotal.toFixed(2)}{t('createOrganization.pricing.perMonth')}</span>
              </div>

              {pricingCalculation.discountPercent > 0 && (
                <div className="summary-line discount">
                  <span>
                    <Percent className="w-4 h-4" />
                    {pricingCalculation.discountRule} ({pricingCalculation.discountPercent}% off)
                  </span>
                  <span className="discount-amount">-${pricingCalculation.discountAmount.toFixed(2)}</span>
                </div>
              )}

              <div className="summary-line total">
                <span>{t('createOrganization.total', 'Total')}</span>
                <span className="total-amount">${pricingCalculation.total.toFixed(2)}{t('createOrganization.pricing.perMonth')}</span>
              </div>

              {form.billingCycle === 'ANNUAL' && (
                <div className="annual-note">
                  {t('createOrganization.annualNote', 'Billed annually at')} ${(pricingCalculation.total * 12).toFixed(2)}{t('createOrganization.pricing.perYear')}
                </div>
              )}
            </div>
          )}

          {/* Additional Options */}
          <div className="form-grid" style={{ marginTop: '20px' }}>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.currency')}</label>
              <select
                name="currency"
                value={form.currency}
                onChange={handleChange}
                className="form-select"
              >
                {currencyOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="form-help">{t('createOrganization.help.currencyNote')}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.trialPeriod')}</label>
              <div className="toggle-row">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    name="startTrial"
                    checked={form.startTrial}
                    onChange={handleChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">{t('createOrganization.fields.enableTrial')}</span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('createOrganization.fields.partnerReferral')}</label>
              <select
                name="partnerId"
                value={form.partnerId}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">{t('createOrganization.fields.noPartner')}</option>
                {partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name} ({partner.commissionRate}%)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="form-section">
          <div className="form-section-title">📝 {t('createOrganization.sections.notes')}</div>
          <div className="form-group">
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              className="form-textarea"
              placeholder={t('createOrganization.placeholders.notes')}
            />
          </div>
        </div>

        {/* Info Alert */}
        <div className="alert-box info">
          <div className="alert-content">
            <div className="alert-title">ℹ️ {t('createOrganization.info.title')}</div>
            <div className="alert-text">
              {t('createOrganization.info.description')}
            </div>
          </div>
        </div>
      </form>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .create-org-page {
    width: 100%;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
  }

  .page-title {
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  .page-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
    }

    .page-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .page-actions .btn {
      padding: 8px 12px;
      font-size: 12px;
    }

    .page-actions .btn-primary .btn-text {
      display: none;
    }
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-brand-primary-hover);
  }

  .btn-secondary {
    background: transparent;
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  /* Form Sections */
  .form-section {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .form-section-title {
    font-family: var(--font-heading);
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-text-primary);
  }

  /* Form Grid */
  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    .form-grid {
      grid-template-columns: 1fr;
    }
  }

  /* Form Group */
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .form-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  .required {
    color: var(--color-error, #EF4444);
  }

  .form-input,
  .form-select,
  .form-textarea {
    width: 100%;
    padding: 0 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    font-size: 13px;
    color: var(--color-text-primary);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .form-input,
  .form-select {
    height: 38px;
  }

  .form-textarea {
    padding: 12px;
    resize: vertical;
    min-height: 80px;
  }

  .form-input:focus,
  .form-select:focus,
  .form-textarea:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px rgba(var(--color-brand-primary-rgb, 139, 0, 0), 0.15);
  }

  /* Field error states */
  .form-input.has-error,
  .form-select.has-error {
    border-color: var(--color-error, #EF4444);
    background: rgba(239, 68, 68, 0.05);
  }

  .form-input.has-error:focus,
  .form-select.has-error:focus {
    border-color: var(--color-error, #EF4444);
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
  }

  .field-error {
    display: block;
    font-size: 11px;
    color: var(--color-error, #EF4444);
    margin-top: 4px;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .form-input.mono {
    font-family: var(--font-mono);
  }

  .form-help {
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  /* Toggle Switch */
  .toggle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
  }

  .toggle-switch {
    position: relative;
    width: 40px;
    height: 22px;
    display: inline-block;
  }

  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 22px;
    transition: all 0.2s ease;
  }

  .toggle-slider::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    left: 2px;
    bottom: 2px;
    background: var(--color-text-muted);
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  .toggle-switch input:checked + .toggle-slider {
    background: var(--color-brand-primary);
    border-color: var(--color-brand-primary);
  }

  .toggle-switch input:checked + .toggle-slider::before {
    transform: translateX(18px);
    background: white;
  }

  .toggle-label {
    font-size: 13px;
    color: var(--color-text-primary);
  }

  /* Alert Box */
  .alert-box {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .alert-box.info {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
  }

  .alert-content {
    flex: 1;
  }

  .alert-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--color-text-primary);
    margin-bottom: 4px;
  }

  .alert-text {
    font-size: 12px;
    color: var(--color-text-secondary);
    line-height: 1.5;
  }

  /* Section Description */
  .section-description {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin-bottom: 16px;
  }

  /* Billing Cycle Toggle */
  .billing-cycle-toggle {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    padding: 4px;
    background: var(--color-bg-primary);
    border-radius: 8px;
    width: fit-content;
  }

  .cycle-btn {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    background: transparent;
    color: var(--color-text-secondary);
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .cycle-btn.active {
    background: var(--color-brand-primary);
    color: white;
  }

  .cycle-btn:hover:not(.active) {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .discount-badge {
    font-size: 11px;
    padding: 2px 6px;
    background: rgba(34, 197, 94, 0.2);
    color: #22C55E;
    border-radius: 4px;
    font-weight: 600;
  }

  .cycle-btn.active .discount-badge {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  /* CORE Module Hero */
  .core-module-hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 8px;
  }

  .core-module-card {
    width: 100%;
    background: linear-gradient(135deg, var(--color-brand-primary) 0%, color-mix(in srgb, var(--color-brand-primary) 80%, #000) 100%);
    border-radius: 16px;
    padding: 24px;
    display: flex;
    align-items: center;
    gap: 20px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(var(--color-brand-primary-rgb, 139, 0, 0), 0.3);
  }

  .core-module-card::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%);
    pointer-events: none;
  }

  .core-icon-wrapper {
    position: relative;
    flex-shrink: 0;
  }

  .core-icon {
    font-size: 48px;
    display: block;
    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
    animation: float 3s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }

  .core-pulse {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    animation: pulse 2s ease-out infinite;
  }

  @keyframes pulse {
    0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.8; }
    100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
  }

  .core-content {
    flex: 1;
    color: white;
    z-index: 1;
  }

  .core-title {
    font-size: 20px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 4px;
  }

  .core-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    background: rgba(255,255,255,0.2);
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .core-description {
    font-size: 14px;
    opacity: 0.9;
    margin-bottom: 8px;
  }

  .core-included {
    font-size: 12px;
    opacity: 0.7;
    font-style: italic;
  }

  .core-check {
    width: 40px;
    height: 40px;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
    z-index: 1;
  }

  .core-connector {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 0;
  }

  .connector-line {
    width: 2px;
    height: 20px;
    background: linear-gradient(to bottom, var(--color-brand-primary), var(--color-border-default));
  }

  .connector-dots {
    display: flex;
    gap: 24px;
    position: relative;
  }

  .connector-dots::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--color-border-default);
    transform: translateY(-50%);
  }

  .connector-dots span {
    width: 8px;
    height: 8px;
    background: var(--color-brand-primary);
    border-radius: 50%;
    position: relative;
    z-index: 1;
  }

  .addon-modules-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 12px;
    text-align: center;
  }

  @media (max-width: 768px) {
    .core-module-card {
      flex-direction: column;
      text-align: center;
      padding: 20px;
    }

    .core-icon {
      font-size: 40px;
    }

    .core-title {
      justify-content: center;
      flex-wrap: wrap;
    }

    .core-check {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
    }
  }

  /* Module Cards */
  .module-cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }

  @media (max-width: 768px) {
    .module-cards {
      grid-template-columns: 1fr;
    }
  }

  .module-card {
    background: var(--color-bg-primary);
    border: 2px solid var(--color-border-default);
    border-radius: 10px;
    padding: 16px;
    transition: all 0.2s ease;
  }

  .module-card.selected {
    border-color: var(--color-brand-primary);
    background: rgba(var(--color-brand-primary-rgb, 139, 0, 0), 0.05);
  }

  .module-card:hover:not(.selected) {
    border-color: var(--color-border-hover);
  }

  .module-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .module-checkbox {
    position: relative;
    cursor: pointer;
    flex-shrink: 0;
  }

  .module-checkbox input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .module-checkbox .checkmark {
    width: 22px;
    height: 22px;
    border: 2px solid var(--color-border-default);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    background: var(--color-bg-primary);
  }

  .module-checkbox input:checked ~ .checkmark {
    background: var(--color-brand-primary);
    border-color: var(--color-brand-primary);
    color: white;
  }

  .module-info {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex: 1;
  }

  .module-icon {
    font-size: 24px;
    line-height: 1;
  }

  .module-name {
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text-primary);
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .module-desc {
    font-size: 12px;
    color: var(--color-text-secondary);
    line-height: 1.4;
  }

  .module-config {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--color-border-subtle);
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .config-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .config-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-secondary);
    min-width: 50px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .tier-select {
    flex: 1;
    height: 34px;
    font-size: 12px;
  }

  .user-count-input {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .count-btn {
    width: 28px;
    height: 28px;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 16px;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .count-btn:hover {
    background: var(--color-bg-hover);
    border-color: var(--color-brand-primary);
  }

  .count-input {
    width: 50px;
    height: 28px;
    text-align: center;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 13px;
  }

  .count-input:focus {
    outline: none;
    border-color: var(--color-brand-primary);
  }

  .limit-info {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .validation-error {
    font-size: 11px;
    color: var(--color-error, #ef4444);
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .input-error {
    border-color: var(--color-error, #ef4444) !important;
  }

  /* Pricing Summary */
  .pricing-summary {
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 10px;
    padding: 16px;
  }

  .summary-title {
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text-primary);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .line-items {
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .line-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    font-size: 13px;
  }

  .item-name {
    color: var(--color-text-secondary);
  }

  .item-name .user-count {
    color: var(--color-text-muted);
    font-size: 12px;
  }

  .item-price {
    color: var(--color-text-primary);
    font-weight: 500;
  }

  .summary-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 13px;
  }

  .summary-line.subtotal {
    color: var(--color-text-secondary);
  }

  .summary-line.discount {
    color: #22C55E;
  }

  .summary-line.discount span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .discount-amount {
    font-weight: 500;
  }

  .summary-line.total {
    border-top: 1px solid var(--color-border-default);
    padding-top: 12px;
    margin-top: 4px;
    font-weight: 600;
    font-size: 15px;
    color: var(--color-text-primary);
  }

  .total-amount {
    color: var(--color-brand-primary);
    font-size: 18px;
  }

  .annual-note {
    text-align: right;
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }
`;
